/**
 * Global utility for downloading files without popup warnings
 * Supports both direct file URLs and API-generated files
 */

export interface DownloadOptions {
  filename?: string
  mimeType?: string
  openInNewTab?: boolean
}

/**
 * Downloads a file from a URL using blob approach (no popup warnings)
 */
export async function downloadFile(
  url: string,
  options: DownloadOptions = {}
): Promise<boolean> {
  try {
    const { filename, mimeType, openInNewTab = false } = options

    if (openInNewTab) {
      // For cases where we want to open in new tab (like CSV exports)
      window.open(url, "_blank")
      return true
    }

    // Fetch the file as blob
    const response = await fetch(url)
    if (!response.ok) return false

    const blob = await response.blob()
    if (blob && blob.size > 0) {
      // Create blob URL and trigger download
      const blobUrl = window.URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = blobUrl
      link.setAttribute("download", filename || "download")

      if (mimeType) {
        link.setAttribute("type", mimeType)
      }

      document.body.appendChild(link)
      link.click()
      link.parentNode?.removeChild(link)
      window.URL.revokeObjectURL(blobUrl)
      return true
    }
    return false
  } catch (error) {
    console.error("Download failed:", error)
    return false
  }
}

/**
 * Downloads a file from API response with file URL
 */
export async function downloadFileFromApi(
  apiResponse: { file: string; label?: string },
  options: DownloadOptions = {}
): Promise<boolean> {
  const { file, label } = apiResponse
  const filename = options.filename || (label ? `${label}.pdf` : "download.pdf")

  return downloadFile(file, { ...options, filename })
}

/**
 * Downloads a PDF file from API response
 */
export async function downloadPdfFromApi(
  apiResponse: { file: string; label?: string },
  filename?: string
): Promise<boolean> {
  return downloadFileFromApi(apiResponse, {
    filename:
      filename ||
      (apiResponse.label ? `${apiResponse.label}.pdf` : "document.pdf"),
    mimeType: "application/pdf"
  })
}

/**
 * Opens a URL in new tab (for non-downloadable content like dashboards)
 */
export function openInNewTab(url: string): void {
  if (url) {
    window.open(url, "_blank")
  }
}

/**
 * Downloads a CSV file from URL using blob approach (no popup warnings)
 */
export async function downloadCsvFromUrl(
  url: string,
  filename: string = "export.csv"
): Promise<boolean> {
  try {
    const response = await fetch(url)
    if (!response.ok) return false

    const csvContent = await response.text()
    if (csvContent) {
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" })
      const blobUrl = window.URL.createObjectURL(blob)
      const link = document.createElement("a")

      link.href = blobUrl
      link.setAttribute("download", filename)
      document.body.appendChild(link)
      link.click()
      link.parentNode?.removeChild(link)
      window.URL.revokeObjectURL(blobUrl)
      return true
    }
    return false
  } catch (error) {
    console.error("CSV download failed:", error)
    return false
  }
}

/**
 * Downloads a CSV file from direct CSV data
 */
export function downloadCsvFromData(
  csvData: string,
  filename: string = "export.csv"
): boolean {
  try {
    if (csvData) {
      const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" })
      const blobUrl = window.URL.createObjectURL(blob)
      const link = document.createElement("a")

      link.href = blobUrl
      link.setAttribute("download", filename)
      document.body.appendChild(link)
      link.click()
      link.parentNode?.removeChild(link)
      window.URL.revokeObjectURL(blobUrl)
      return true
    }
    return false
  } catch (error) {
    console.error("CSV download failed:", error)
    return false
  }
}
